import { Entity, mapEntity, PriceHistory } from '@toggle/toggle';
import clsx from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useFlags } from '~/stores/use-flags/useFlags';
import { globalPriceFormatter } from '~/utils/asset-price/assetPrice';

import { PriceSummaryDisplay } from '../components/PriceSummaryDisplay';
import styles from './EarningsMarketReactionPrice.module.scss';

const EASTERN_TIMEZONE = 'America/New_York';

const getEasternTimezoneAbbr = (date: Date): string => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIMEZONE,
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(date);
  const tzPart = parts.find(part => part.type === 'timeZoneName');
  return tzPart?.value ?? 'ET';
};

export interface EarningsMarketReactionPriceProps {
  entity: Entity;
  isWindowComplete: boolean;
  eventProportionChange?: number;
  prevCloseData?: PriceHistory[number];
  lastKnownPrice?: number;
  lastKnownDate?: string;
}

export const EarningsMarketReactionPrice = ({
  entity,
  isWindowComplete,
  eventProportionChange,
  prevCloseData,
  lastKnownPrice,
  lastKnownDate,
}: EarningsMarketReactionPriceProps) => {
  const isUniversalDrawerEnabled = useFlags(state =>
    state.getFeatureFlagState('universal_drawer')
  );
  const { t } = useTranslation(['analyze']);

  if (eventProportionChange === undefined) {
    return null;
  }

  const mappedEntity = mapEntity(entity);

  const formatPrice = (price: number) =>
    globalPriceFormatter({
      entity: mappedEntity,
      price,
      priceSuffix: '',
      shouldShowCurrency: false,
    });

  const priceColumn = {
    price: lastKnownPrice ? formatPrice(lastKnownPrice) : undefined,
    currency: mappedEntity.currency,
    label: lastKnownDate && (
      <span
        className={clsx(styles.dateSpan, {
          [styles.dateSpanV2]: isUniversalDrawerEnabled,
        })}
      >
        {t('analyze:analysis.asOfDate', {
          date: `${formatInTimeZone(
            new Date(lastKnownDate),
            EASTERN_TIMEZONE,
            'MMM d, h:mm aaa'
          )} ${getEasternTimezoneAbbr(new Date(lastKnownDate))}`,
        })}
      </span>
    ),
  };

  return (
    <div data-testid="market-reaction-price">
      <div
        className={clsx(styles.proportionChangeContainer, {
          [styles.proportionChangeContainerV2]: isUniversalDrawerEnabled,
        })}
      >
        <PriceSummaryDisplay
          priceColumn={priceColumn}
          percentageChange={eventProportionChange}
          prevCloseTime={prevCloseData?.time}
        />
      </div>
    </div>
  );
};
